/**
 * PreferencesStore
 * This class manages common functionalities such as language, version, and user settings.
 * Developer: Aashish Singh
 * Created Date: 29-March-2023
 * No parameters passed to constructor.
 * Returns: class instance
 */

import { action, computed, makeObservable, observable } from "mobx";
import URLConstants from "../../constants/url-constants";
import config from "../../helpers/config-helper";
import IApiResponse from "../../models/response/IApiResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { IPreferenceState } from "../../models/state/IPreferenceState";
import * as baseService from "../service/base-service";

// Get app configuration and default language
const appConfig = config();
const defaultLanguage = appConfig.REACT_APP_DEFAULT_LANGUAGE;

/**
 * PreferencesStore class
 */
export class PreferencesStore implements IPreferenceState, ICommonState {
  // Observable state
  inProgress = false;
  error = "";
  lang =
    localStorage.getItem("lizera_language") !== null
      ? localStorage.getItem("lizera_language")
      : "jp";
  language = this.lang!;
  version = "";
  userSetting: any = {
    pageLimitConfig:
      localStorage.getItem("lizera_pageLimitConfig") !== null
        ? parseInt(localStorage.getItem("lizera_pageLimitConfig")!)
        : 100,
    tagPageLimitConfig:
      localStorage.getItem("lizera_tagPageLimitConfig") !== null
        ? parseInt(localStorage.getItem("lizera_tagPageLimitConfig")!)
        : 10,
  };
  islanguageChanged = false;

  constructor() {
    makeObservable(this, {
      language: observable,
      version: observable,
      userSetting: observable,
      islanguageChanged: observable,
      getLanguage: computed,
      getVersionFromStore: computed,
      changeLanguage: action,
      UpdateLanguage: action,
      updateLanguageChangedFlag: action,
      setUserSetting: action,
    });
  }

  /**
   * Computed: Get current language
   */
  get getLanguage() {
    return this.language;
  }

  /**
   * Change the application language.
   * @param language - New language code
   */
  changeLanguage(language: string) {
    this.islanguageChanged = this.language === language ? false : true;
    this.language = language;
    localStorage.setItem("lizera_language", language);
  }

  /**
   * Update language from localStorage.
   */
  UpdateLanguage() {
    const language = localStorage.getItem("lizera_language");
    if (language) this.language = language;
  }

  /**
   * Reset the language changed flag.
   */
  updateLanguageChangedFlag = () => {
    this.islanguageChanged = false;
  };

  /**
   * Fetch version from API and update store/localStorage.
   * @returns Promise<void>
   */
  getVersion = () => {
    this.inProgress = true;
    const url = URLConstants.Version;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<string>) => {
        // Format version to one decimal place
        const data = Number(response.data).toFixed(1);
        this.version = data;
        localStorage.setItem("lizera_version", data);
      })
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * Computed: Get version from store or localStorage.
   */
  get getVersionFromStore() {
    return this.version !== ""
      ? this.version
      : localStorage.getItem("lizera_version");
  }

  /**
   * Set user configuration setting.
   * @param key - Setting key
   * @param id - Setting value
   */
  setUserSetting = (key: string, id: number) => {
    localStorage.setItem("lizera_" + key, id.toString());
    this.userSetting[key] = id;
  };
}

export default new PreferencesStore();
